import React, { Fragment, useEffect, useRef, useState } from "react";
import { Button, FormGroup, Label } from 'reactstrap';
import Select, { components } from "react-select";

const TrimmingsTable = (props) => {
    const { setValue, trigger, settingTrimmings, settingSubTrimmings, trimmingRows, setTrimmingRows } = props;

    // New: duplicate tracking state
    const [duplicateIndices, setDuplicateIndices] = useState([]);

    const handleTrimmingRowChange = (idx, field, value) => {
        // If parent trimming changes, reset sub_trimming so the sub-select refreshes
        if (field === 'trimming') {
            setTrimmingRows(prev => prev.map((row, i) => i === idx ? { ...row, trimming: value, sub_trimming: [] } : row));
        } else {
            setTrimmingRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
        }
    };

    const handleAddTrimmingRow = () => {
        setTrimmingRows(prev => ([...prev, { trimming: '', sub_trimming: [] }]));
    };

    const handleRemoveTrimmingRow = (idx) => {
        setTrimmingRows(prev => prev.length > 0 ? prev.filter((_, i) => i !== idx) : prev);
    };

    useEffect(() => {
        setValue("trimmings", [...trimmingRows]);
        trigger("trimmings");
    }, [trimmingRows]);

    // New: detect duplicate rows & expose a form-level flag
    useEffect(() => {
        const keyMap = {};
        (trimmingRows || []).forEach((row, idx) => {
            const sub = (row.sub_trimming || []).slice().sort().join(',');
            const key = `${row.trimming || ''}|${sub}`;
            const isEmpty = !row.trimming && (!row.sub_trimming || row.sub_trimming.length === 0);
            if (isEmpty) return; // ignore totally empty rows
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push(idx);
        });
        const duplicates = new Set();
        Object.values(keyMap).forEach(arr => { if (arr.length > 1) arr.forEach(i => duplicates.add(i)); });
        const dupArray = Array.from(duplicates);
        setDuplicateIndices(dupArray);
        const hasDuplicates = dupArray.length > 0;
        if (setValue) setValue("trimmings_has_duplicates", hasDuplicates);
        if (trigger) trigger("trimmings_has_duplicates");
    }, [trimmingRows, setValue, trigger]);

    return (
        <FormGroup>
            <Label className="form-label ai-form-heading">Trimmings Details</Label>
            <table className="table table-bordered table-sm align-middle">
                <thead>
                    <tr>
                        <th style={{ width: '40%' }}>Trimmings</th>
                        <th style={{ width: '40%' }}>Sub Trimmings</th>
                        <th style={{ width: '20%' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {(trimmingRows || []).map((row, idx) => {
                        const isDuplicate = duplicateIndices.includes(idx);

                        // helper to pass styles to react-select so only the select control shows error state
                        const selectErrorStyles = {
                            control: (base, state) => ({
                                ...base,
                                borderColor: isDuplicate ? '#dc3545' : base.borderColor,
                                boxShadow: isDuplicate ? '0 0 0 1px rgba(220,53,69,0.12)' : base.boxShadow,
                                '&:hover': { borderColor: isDuplicate ? '#dc3545' : base.borderColor },
                            })
                        };

                        return (
                            <tr key={idx}>
                                <td>
                                    <Select
                                        placeholder="Select Trimming"
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        options={settingTrimmings}
                                        value={settingTrimmings.find(opt => opt.id === row.trimming) || null}
                                        onChange={selected => handleTrimmingRowChange(idx, 'trimming', selected ? selected.id : '')}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    <Select
                                        placeholder="Select Sub Trimmings"
                                        isMulti
                                        isDisabled={!row.trimming} // disable until a trimming is selected
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        options={settingSubTrimmings.filter(opt => opt.trimming_id === row.trimming)}
                                        value={settingSubTrimmings.filter(opt => (row.sub_trimming || []).includes(opt.id))}
                                        onChange={selected => handleTrimmingRowChange(idx, 'sub_trimming', selected ? selected.map(s => s.id) : [])}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td className="text-center">
                                    <Button 
                                        color="danger" 
                                        size="sm" 
                                        onClick={() => handleRemoveTrimmingRow(idx)} 
                                    ><i className="fa fa-trash" /></Button>
                                    {isDuplicate && <div className="text-danger small mt-1">Duplicate row</div>}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <Button color="success" size="sm" className="mt-2" onClick={handleAddTrimmingRow}><i className="fa fa-plus" /> Add Row</Button>
        </FormGroup>
    );
};

export default TrimmingsTable;
